import React, { useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Alert, Box } from '@mui/material';
import { parseIFrameEvent } from 'utilities/parseIFrameEvent';
import {
  addFlowBreadcrumb,
  recordIframeEvent,
  recordIframeLoad,
  recordPaymentFormUrl,
  trackIframeRetryLimitReached,
} from 'utilities/paymentFlowTelemetry';
import Styles from './PaymentIframe.module.scss';

const MAX_IFRAME_RETRIES = 6;


export interface Props {
  orderId: number;
  onPaymentFailure: (code: string, message: string) => void;
  onTokenReceived: (token: string) => Promise<void>;
  payFormUrl: string;
  paymentError?: (string | null)[];
  paymentProcessing: boolean;
}

const PaymentIframe = ({
  orderId,
  payFormUrl,
  paymentError = [],
  paymentProcessing,
  onPaymentFailure,
  onTokenReceived,
}: Props): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [iframeKey, setIframeKey] = useState<number>(1);

  const timeoutRef = useRef<number>();
  const onPaymentFailureRef = useRef(onPaymentFailure);
  const onTokenReceivedRef = useRef(onTokenReceived);

  useEffect(() => {
    onPaymentFailureRef.current = onPaymentFailure;
    onTokenReceivedRef.current = onTokenReceived;
  }, [onPaymentFailure, onTokenReceived]);

  // Telemetry: remember which hosted transaction this iframe shows, and note
  // when the iframe leaves the page (e.g. Base stopped rendering the flow).
  useEffect(() => {
    if (payFormUrl) recordPaymentFormUrl(payFormUrl);
  }, [payFormUrl]);

  useEffect(() => {
    return () => addFlowBreadcrumb('payment iframe unmounted');
  }, []);

  useEffect(() => {
    const handleIframeTask = (event: MessageEvent): void => {
      const {
        eventType,
        token,
        code,
        reason
      } = parseIFrameEvent(event, payFormUrl);

      const timeDelay = (2 ** iframeKey) * 100;

      if (eventType==='success') {
        recordIframeEvent('success');
        onTokenReceivedRef.current(token);
      } else if (eventType === 'failure') {
        recordIframeEvent('failure', code, reason);

        // Let the caller know what the failure was.
        onPaymentFailureRef.current(code, reason);

        // If an error occurred, this will redraw the iframe with the
        // payment gateway and re-register the event listeners.
        // It will only respond to the first 5 error, then give up.
        if(iframeKey < MAX_IFRAME_RETRIES)
        {
          timeoutRef.current = window.setTimeout(() => {
            setIframeKey((prevIframeKey) => prevIframeKey + 1);
          }, timeDelay);
        } else {
          trackIframeRetryLimitReached({ iframeKey, code, reason });
        }
      }
    };
    
    // eslint-disable-next-line no-console
    console.log(`[PaymentIframe] Listening for messages from iframe with key ${iframeKey}`);
    window.addEventListener('message', handleIframeTask, false);

    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('message', handleIframeTask);
    };
  }, [payFormUrl, iframeKey]);

  return (
    <div>
      {!paymentProcessing &&
        paymentError &&
        paymentError.length > 0 &&
        paymentError.map(
          (message, index) =>
            message && (
              <Alert key={index} severity="error" sx={{ marginY: '1rem' }}>
                {message}
              </Alert>
            )
        )}
      <div className={Styles.wrapper}>
        {(loading || paymentProcessing) && (
          <Box
            role="status"
            sx={{
              margin: '4rem auto',
              textAlign: 'center',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            className="loading"
          >
            <CircularProgress
              data-testid="loading-spinner"
              color="info"
              aria-label="Loading"
            />
          </Box>
        )}
        {payFormUrl && !paymentProcessing && (
          <iframe
            key={iframeKey}
            title={`Order #${orderId}`}
            data-testid="payment-iframe"
            referrerPolicy="origin"
            src={payFormUrl}
            className={Styles.pay_form_iframe}
            onLoad={() => {
              setLoading(false);
              recordIframeLoad(iframeKey);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentIframe;
